import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  Wallet,
  Bell,
  ChevronDown,
  LogOut,
  LogIn,
  Search,
} from "lucide-react";
import { Menu, MenuItem } from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import SimpleSnackbar from "../Snackbar";
import usePushNotifications from "../../hooks/usePushNotification";
import { DOCTOR_UNREAD_MESSAGES_EVENT } from "../../hooks/useDoctorUnreadMessages";

let socket;

const DoctorHeader = () => {
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const [wallet, setWallet] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const [walletLoading, setWalletLoading] = useState(true);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");

  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("info");
  const [isMessage, setIsMessage] = useState(false);
  const [client__id, setClient__id] = useState(null);

  const [timer, setTimer] = useState(15 * 60);
  const timerRef = useRef(null);

  const navigate = useNavigate();
  const doctor = JSON.parse(localStorage.getItem("doctor") || "{}");

  const firstName = doctor?.name?.split(" ")?.[0] || "Doctor";
  const initials =
    doctor?.name
      ?.split(" ")
      ?.map((item) => item[0])
      ?.join("")
      ?.slice(0, 2)
      ?.toUpperCase() || "DR";

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  const showNotification = (title, body, clientId) => {
    if (Notification.permission !== "granted") return;

    const notification = new Notification(title, {
      body,
      icon: "/icon-192.png",
      data: { clientId },
      tag: false,
    });

    notification.onclick = () => {
      window.focus();
      navigate(`/dashboard/patient/${clientId}`);
    };
  };

  const getWalletBalance = async () => {
    try {
      setWalletLoading(true);

      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/doctor-wallet",
        { doctorId: doctor?._id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      setWallet({ balance: res.data.wallet });
    } catch (err) {
      console.error(err);
      setSnackMessage("Failed to fetch wallet balance");
      setSnackColor("error");
    } finally {
      setWalletLoading(false);
    }
  };

  const getNotifications = useCallback(async () => {
    try {
      setNotificationsLoading(true);

      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/doctor/notifications",
        { doctorId: doctor?._id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      setNotifications(res.data.notifications.slice(0, 5));
    } catch (err) {
      console.error(err);
      setSnackMessage("Failed to fetch notifications");
      setSnackColor("error");
    } finally {
      setNotificationsLoading(false);
    }
  }, [doctor?._id]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    getWalletBalance();
    getNotifications();
  }, [getNotifications]);

  useEffect(() => {
    const refreshNotifications = () => {
      getNotifications();
    };
    const interval = setInterval(refreshNotifications, 15000);

    window.addEventListener(DOCTOR_UNREAD_MESSAGES_EVENT, refreshNotifications);
    window.addEventListener("focus", refreshNotifications);

    return () => {
      clearInterval(interval);
      window.removeEventListener(
        DOCTOR_UNREAD_MESSAGES_EVENT,
        refreshNotifications
      );
      window.removeEventListener("focus", refreshNotifications);
    };
  }, [getNotifications]);

  useEffect(() => {
    const doctorStr = localStorage.getItem("doctor");

    if (!doctorStr) {
      navigate("/");
      return;
    }

    const checkDoctorVerification = async () => {
      const doctor = JSON.parse(doctorStr);

      if (!doctor.isVerified) {
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/doctor/get`,
            { id: doctor._id },
            {
              headers: {
                Authorization: `Bearer ${
                  localStorage.getItem("token") || null
                }`,
              },
            }
          );

          if (res.data.doctor && !res.data.doctor.isVerified) {
            navigate("/dashboard/verification");
          } else if (window.location.pathname === "/dashboard/verification") {
            navigate("/dashboard/home");
          }
        } catch (err) {
          console.error("Verification check error", err);
        }
      } else if (window.location.pathname === "/dashboard/verification") {
        navigate("/dashboard/home");
      }
    };

    checkDoctorVerification();
  }, [navigate]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;

    return `${m.toString().padStart(2, "0")}:${s
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    const handleFocus = () => {
      if (!socket) {
        socket = io(process.env.REACT_APP_BACKEND_URL, {
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 3000,
          auth: {
            token: localStorage.getItem("token"),
          },
        });
      }

      const handleConnect = () => {
        setMessage("Socket connected");
        setStatus("success");
        socket.emit("join_doctor_room", doctor._id);
      };

      const handleConnectError = (err) => {
        console.log("Socket connection failed:", err.message);
        setMessage("Connection lost. Retrying...");
        setStatus("error");
      };

      const handleNewMessage = (msg) => {
        if (msg.doctor_id !== doctor._id) return;

        setMessage(`New message from ${msg.sender_name}`);
        setIsMessage(true);
        setClient__id(msg.sender_id);
        setStatus("success");

        getNotifications();

        showNotification(
          "New Patient Message",
          `${msg.sender_name}: ${msg.text || "New message received"}`,
          msg.sender_id
        );

        if (Notification.permission === "granted") {
          new Notification("New Message from " + msg.sender_name, {
            body: msg.text || "New message received",
            icon: "/icon-192.png",
          });
        }

        setTimeout(() => {
          setMessage(null);
          setIsMessage(false);
        }, 4000);
      };

      socket.on("connect", handleConnect);
      socket.on("connect_error", handleConnectError);
      socket.on("new_message", handleNewMessage);
    };

    window.addEventListener("focus", handleFocus);

    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  useEffect(() => {
    if (!doctor?._id) return;

    if (!socket) {
      socket = io(process.env.REACT_APP_BACKEND_URL, {
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 3000,
        auth: {
          token: localStorage.getItem("token"),
        },
      });
    }

    const handleConnect = () => {
      setMessage("Socket connected");
      setStatus("success");
      socket.emit("join_doctor_room", doctor._id);
    };

    const handleConnectError = (err) => {
      console.log("Socket connection failed:", err.message);
      setMessage("Connection lost. Retrying...");
      setStatus("error");
    };

    const handleNewMessage = (msg) => {
      if (msg.doctor_id !== doctor._id) return;

      setMessage(`New message from ${msg.sender_name}`);
      setIsMessage(true);
      setClient__id(msg.sender_id);
      setStatus("success");

      getNotifications();

      setTimeout(() => {
        setMessage(null);
        setIsMessage(false);
      }, 4000);
    };

    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
    socket.on("new_message", handleNewMessage);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.off("new_message", handleNewMessage);
    };
  }, [doctor?._id]);

  const handleProfileClick = (e) => setProfileAnchor(e.currentTarget);
  const handleProfileClose = () => setProfileAnchor(null);

  const handleWalletClick = () =>
    alert(
      "Hey, there isn't a wallet now as we are still onboarding. Work with us lets make this grow Dr " +
        JSON.parse(localStorage.getItem("doctor"))?.name.split(" ")[0] +
        "!"
    );

  const handleNotificationsClick = async () => {
    setNotificationsOpen((prev) => !prev);
    if (!notifications.length) getNotifications();
  };

  usePushNotifications(doctor?._id);

  return (
    <>
      <SimpleSnackbar
        message={message}
        color={status}
        isMessage={isMessage}
        client__id={client__id}
      />

      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-xl">
        <div className="flex min-h-[76px] items-center justify-between gap-4 px-4 sm:px-6">
          {/* LEFT */}
          <div className="min-w-0">
            <p className="text-xs font-medium text-slate-500">
              Welcome back, Dr. {firstName}
            </p>

            <h1 className="mt-1 truncate text-xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-2xl">
              Dashboard
            </h1>
          </div>

          {/* CENTER SEARCH - DESKTOP ONLY */}
          {/* <div className="hidden min-w-[280px] max-w-md flex-1 lg:block">
            <div className="flex h-11 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4">
              <Search size={17} className="text-slate-400" />
              <input
                type="text"
                placeholder="Search patients..."
                className="w-full bg-transparent text-sm font-medium text-slate-700 outline-none placeholder:text-slate-400"
                onFocus={() => navigate("/dashboard/home")}
              />
            </div>
          </div> */}

          {/* RIGHT */}
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {/* Wallet */}
            <button
              type="button"
              onClick={handleWalletClick}
              className="hidden h-11 items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 transition hover:bg-slate-50 sm:flex"
            >
              <Wallet size={17} className="text-slate-500" />

              <div className="text-left leading-tight">
                <p className="text-[11px] font-medium text-slate-400">
                  Wallet
                </p>

                {walletLoading ? (
                  <div className="mt-1 h-3 w-14 rounded bg-slate-200" />
                ) : (
                  <p className="text-sm font-semibold text-slate-900">
                    ₦{wallet?.balance?.toLocaleString() || "0"}
                  </p>
                )}
              </div>
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                type="button"
                onClick={handleNotificationsClick}
                className="relative flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
              >
                <Bell size={18} />

                {notifications.length > 0 && (
                  <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-500" />
                )}
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 mt-3 w-[300px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl sm:w-[340px]">
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                    <p className="text-sm font-semibold text-slate-950">
                      Notifications
                    </p>

                    <button
                      type="button"
                      onClick={() => setNotificationsOpen(false)}
                      className="text-xs font-medium text-slate-400 hover:text-slate-700"
                    >
                      Close
                    </button>
                  </div>

                  <div className="max-h-[360px] overflow-y-auto">
                    {notificationsLoading && (
                      <div className="p-5 text-center text-sm text-slate-400">
                        Loading...
                      </div>
                    )}

                    {!notificationsLoading && notifications.length === 0 && (
                      <div className="p-5 text-center text-sm text-slate-400">
                        No notifications
                      </div>
                    )}

                    {!notificationsLoading &&
                      notifications.map((n, i) => (
                        <button
                          key={i}
                          type="button"
                          className="flex w-full gap-3 border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-slate-50"
                          onClick={() => {
                            if (n.type === "patient_message") {
                              navigate(`/dashboard/patient/${n.client_id}`);
                            }
                            setNotificationsOpen(false);
                          }}
                        >
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
                            {n.name?.slice(0, 1).toUpperCase() || "N"}
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {n.name || "Notification"}
                            </p>

                            <p className="mt-0.5 line-clamp-2 text-xs leading-5 text-slate-500">
                              {n.type === "new_patient_alert"
                                ? `${n.message} - ${formatTime(timer)}`
                                : n.message}
                            </p>

                            {n.timeAgo && (
                              <p className="mt-1 text-[11px] text-slate-400">
                                {n.timeAgo}
                              </p>
                            )}
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Profile */}
            <button
              type="button"
              onClick={handleProfileClick}
              className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 transition hover:bg-slate-50 sm:px-3"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                {initials}
              </div>

              <div className="hidden text-left leading-tight md:block">
                <p className="max-w-[120px] truncate text-sm font-semibold text-slate-900">
                  Dr. {firstName}
                </p>
                <p className="text-[11px] text-slate-400">Doctor</p>
              </div>

              <ChevronDown size={15} className="text-slate-400" />
            </button>

            <Menu
              anchorEl={profileAnchor}
              open={Boolean(profileAnchor)}
              onClose={handleProfileClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              {localStorage.getItem("doctor") ? (
                <MenuItem
                  onClick={() => {
                    localStorage.clear();
                    navigate("/");
                  }}
                >
                  <LogOut size={16} className="mr-2" />
                  Logout
                </MenuItem>
              ) : (
                <MenuItem onClick={handleProfileClose}>
                  <LogIn size={16} className="mr-2" />
                  Login
                </MenuItem>
              )}
            </Menu>
          </div>
        </div>
      </header>
    </>
  );
};

export default DoctorHeader;
